import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { carriages } from '../mock';

export const Attelages = () => {
  const [selectedCarriage, setSelectedCarriage] = useState(carriages[0].id);
  const [currentImageIndices, setCurrentImageIndices] = useState({});

  const nextImage = (carriageId, totalImages) => {
    setCurrentImageIndices(prev => ({
      ...prev,
      [carriageId]: ((prev[carriageId] || 0) + 1) % totalImages
    }));
  };

  const prevImage = (carriageId, totalImages) => {
    setCurrentImageIndices(prev => ({
      ...prev,
      [carriageId]: ((prev[carriageId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  return (
    <div className="attelages-page">
      {/* Header */}
      <section className="page-header">
        <div className="container">
          <h1 className="page-title">Nos Attelages</h1>
          <p className="page-description">
            Deux modèles innovants pour répondre à tous les besoins d'accessibilité.
          </p>
        </div>
      </section>

      {/* Carriages Detail */}
      <section className="carriages-detail-section">
        <div className="container">
          <Tabs value={selectedCarriage} onValueChange={setSelectedCarriage} className="carriages-tabs">
            <TabsList className="tabs-list">
              {carriages.map((carriage) => (
                <TabsTrigger key={carriage.id} value={carriage.id} className="tab-trigger">
                  {carriage.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {carriages.map((carriage) => {
              const currentIndex = currentImageIndices[carriage.id] || 0;
              return (
                <TabsContent key={carriage.id} value={carriage.id} className="tab-content">
                  <div className="carriage-detail-grid">
                    {/* Image Gallery */}
                    <div className="image-gallery">
                      <div className="gallery-main">
                        <img
                          src={carriage.images[currentIndex]}
                          alt={`${carriage.name} - Image ${currentIndex + 1}`}
                          className="gallery-image"
                        />
                        <button
                          onClick={() => prevImage(carriage.id, carriage.images.length)}
                          className="gallery-nav prev"
                          aria-label="Image précédente"
                        >
                          <ChevronLeft size={24} />
                        </button>
                        <button
                          onClick={() => nextImage(carriage.id, carriage.images.length)}
                          className="gallery-nav next"
                          aria-label="Image suivante"
                        >
                          <ChevronRight size={24} />
                        </button>
                      </div>
                      <div className="gallery-thumbnails">
                        {carriage.images.map((image, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndices(prev => ({ ...prev, [carriage.id]: index }))}
                            className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                          >
                            <img src={image} alt={`Thumbnail ${index + 1}`} />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Details */}
                    <div className="carriage-details">
                      <h2 className="carriage-detail-name">{carriage.name}</h2>
                      <p className="carriage-detail-subtitle">{carriage.subtitle}</p>
                      <p className="carriage-detail-description">{carriage.description}</p>

                      <div className="specs-grid">
                        <Card className="spec-card">
                          <CardContent className="spec-card-content">
                            <span className="spec-label">Poids</span>
                            <span className="spec-value">{carriage.weight}</span>
                          </CardContent>
                        </Card>
                        <Card className="spec-card">
                          <CardContent className="spec-card-content">
                            <span className="spec-label">Capacité</span>
                            <span className="spec-value">{carriage.capacity}</span>
                          </CardContent>
                        </Card>
                        {carriage.loadCapacity && (
                          <Card className="spec-card">
                            <CardContent className="spec-card-content">
                              <span className="spec-label">Capacité d'emport</span>
                              <span className="spec-value">{carriage.loadCapacity}</span>
                            </CardContent>
                          </Card>
                        )}
                        <Card className="spec-card full-width">
                          <CardContent className="spec-card-content">
                            <span className="spec-label">Type de cheval</span>
                            <span className="spec-value">{carriage.horseType}</span>
                          </CardContent>
                        </Card>
                      </div>

                      <div className="features-list">
                        <h3 className="features-title">Caractéristiques</h3>
                        <ul className="features">
                          {carriage.features.map((feature, index) => (
                            <li key={index} className="feature-item">
                              <Check size={20} className="check-icon" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Link to="/contact">
                        <Button size="lg" className="cta-primary w-full">
                          Demande d'informations / Pré-commander
                          <ChevronRight className="ml-2" size={20} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* Comparison */}
      <section className="comparison-section">
        <div className="container">
          <h2 className="section-title">Comparaison des Attelages</h2>
          <div className="comparison-table">
            <Card>
              <CardContent className="p-0">
                <table className="comparison">
                  <thead>
                    <tr>
                      <th>Caractéristique</th>
                      <th>Tapis Volant</th>
                      <th>Ben-Hur</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Roues</td>
                      <td>3 roues</td>
                      <td>2 roues</td>
                    </tr>
                    <tr>
                      <td>Poids</td>
                      <td>120 kg</td>
                      <td>60 kg</td>
                    </tr>
                    <tr>
                      <td>Capacité</td>
                      <td>1 à 3 personnes</td>
                      <td>1 personne</td>
                    </tr>
                    <tr>
                      <td>Usage principal</td>
                      <td>Fauteuil roulant</td>
                      <td>Meneur debout</td>
                    </tr>
                    <tr>
                      <td>Transport</td>
                      <td>Remorque recommandée</td>
                      <td>Coffre de break</td>
                    </tr>
                    <tr>
                      <td>Type cheval</td>
                      <td>Poney/cheval moyen</td>
                      <td>Tous types</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Attelages;
